import { spacing } from '@scality/core-ui';
import { Box, Button } from '@scality/core-ui/dist/next';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { GuardianContext } from '../GuardianProvider';
import { SupportPromptGroupMore } from './SupportPromptGroupMore';

interface SupportPromptGroupProps {
  onSubmit: (message: string) => void;
}

export const SupportPromptGroup: React.FC<SupportPromptGroupProps> = ({ onSubmit }) => {
  const { followUpQuestions } = useContext(GuardianContext);
  const ref = React.useRef<HTMLDivElement>(null);
  const moreFollowUpRef = React.useRef<HTMLDivElement>(null);
  const [visibleButtonCount, setVisibleButtonCount] = React.useState(0);

  const updateVisibleButtonCount = useCallback(() => {
    if (!ref.current) return;

    const containerRect = ref.current.getBoundingClientRect();
    const containerWidth = containerRect.width;

    const followUpButtons = Array.from(ref.current.children).filter(
      (child) => child !== moreFollowUpRef.current,
    ) as HTMLButtonElement[];

    // The number of visible button is the value of i
    // for which the button i would overflow the container
    for (let i = 0; i < followUpButtons.length; i++) {
      const child = followUpButtons[i];
      const childRect = child.getBoundingClientRect();

      // 56px is the width of the "More options" button plus margin
      if (childRect.right - containerRect.left + 56 > containerWidth) {
        setVisibleButtonCount(i);
        return;
      }
    }

    return setVisibleButtonCount(followUpButtons.length);
  }, []);

  useEffect(() => {
    if (!ref.current) return;

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateVisibleButtonCount);
    });
    resizeObserver.observe(ref.current);

    return () => resizeObserver.disconnect();
  }, [updateVisibleButtonCount]);

  // we update the visible button count when the follow up questions change
  useEffect(() => {
    requestAnimationFrame(updateVisibleButtonCount);
  }, [followUpQuestions, updateVisibleButtonCount]);

  const handleClick = useCallback(
    (followUpQuestion: React.MouseEvent<HTMLButtonElement>) => {
      const question = followUpQuestions?.find((q) => q.id === followUpQuestion.currentTarget.value);
      if (question?.prompt) {
        onSubmit(question.prompt);
      } else {
        window.open(question?.url, '_blank', 'noopener,noreferrer');
      }
    },
    [onSubmit, followUpQuestions],
  );

  const moreFollowUpQuestions = useMemo(
    () => followUpQuestions?.slice(visibleButtonCount) ?? [],
    [followUpQuestions, visibleButtonCount],
  );

  if (!followUpQuestions?.length) {
    return null;
  }

  return (
    <Box display="flex" gap={spacing.r8} ref={ref} height={spacing.f28}>
      {followUpQuestions?.map((followupquestion, i) => (
        <React.Fragment key={followupquestion.id}>
          {i === visibleButtonCount && visibleButtonCount < followUpQuestions.length && (
            <div ref={moreFollowUpRef}>
              <SupportPromptGroupMore onClick={handleClick} followUpQuestions={moreFollowUpQuestions} />
            </div>
          )}
          <Button
            key={followupquestion.id}
            label={followupquestion.title}
            variant="primary"
            value={followupquestion.id}
            onClick={handleClick}
            // We use visibility hidden instead of conditional rendering
            // to keep the button in the layout so that we can measure the buttons width
            style={{ visibility: i < visibleButtonCount ? 'visible' : 'hidden' }}
          />
        </React.Fragment>
      ))}
    </Box>
  );
};
